import {
  Autocomplete,
  Button,
  Fade,
  Grid,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import { get, set, startCase, unionBy } from "lodash";
import { useContext, useEffect, useState } from "react";
import useAsyncEffect from "use-async-effect";
import { medicalsApi } from "../../../../api/medicalsApi";
import { patientApi } from "../../../../api/patientApi";
import { DuplicatePatientModalContext } from "../../../../contexts/DuplicatePatientModalProvider";
import useComponentLoader from "../../../../hooks/useComponentLoader";
import { usePractitionerReference } from "../../../../hooks/usePractitioner";
import { formatDate } from "../../../../pages/appointment/helpers/formatDate";
import { useOrganizationReference } from "../../../../pages/facility/hooks/useOrganization";
import { Patient } from "../../../../types/patient";
import { Reference } from "../../../../types/reference";
import { generateHumanDisplayName } from "../../../../utils/DataTransformationUtils";
import { displayAge } from "../../../../utils/DateUtils";
import {
  NotificationVariant,
  NotificationsContext,
} from "../../../dashboard/NotificationsProvider";
import PatientInput from "../../../form/PatientInput";

type Props = {
  subject: Reference;
  setSubject: (subject: Reference) => void;
  setCurrentEncounterId?: (encounterId: string | undefined) => void;
  setEncounterSearchComplete?: (complete: boolean) => void;
  /** When false, skip open-encounter lookup (e.g. standalone AI Notes). */
  checkOpenEncounters?: boolean;
  /** Full patient record when selected/created (for demographic display). */
  onPatientChange?: (patient: Patient | undefined) => void;
  /** Label for the new-patient submit button. */
  addPatientButtonText?: string;
  /** Toast after successfully registering a new patient. */
  patientCreatedMessage?: string;
};

const FindOrCreatePatientInput = ({
  subject,
  setSubject,
  setCurrentEncounterId,
  setEncounterSearchComplete,
  checkOpenEncounters = true,
  onPatientChange,
  addPatientButtonText = "Add new patient to Encounter",
  patientCreatedMessage = "Successfully registered and added the new patient to the Encounter.",
}: Props) => {
  const defaultPatient = {
    name: [{}],
    telecom: [{ system: "phone", use: "mobile" }],
    gender: "",
    birthDate: null,
  };
  const [patientSearchString, setPatientSearchString] = useState<string>("");
  const [newPatientOption, setNewPatientOption] = useState<boolean>(false);
  const [allowNewPatient, setAllowNewPatient] = useState<boolean>(false);
  const [patientSearchResults, setPatientSearchResults] = useState<Patient[]>(
    [],
  );
  const { setNotification } = useContext(NotificationsContext);
  const [, setComponentLoading] = useComponentLoader();
  const [newPatient, setNewPatient] = useState<any>(defaultPatient);
  const organizationReference = useOrganizationReference();
  const practitionerReference = usePractitionerReference();
  const {
    openDuplicatePatientModal,
    setDuplicatePatients,
    setDuplicatePatientActions,
    closeDuplicatePatientModal,
    setPatientForCreation,
  } = useContext(DuplicatePatientModalContext);

  const searchForPatient = async (): Promise<void> => {
    try {
      const searchResults = await patientApi.findPatientsByOrg({
        q: patientSearchString,
        "managing-organization": organizationReference?.identifier,
      });
      if (searchResults) {
        setPatientSearchResults(searchResults);
      }
      // Don't offer "Create New patient" while the field is showing a selected patient label.
      if (
        !patientSearchString ||
        patientSearchString.length === 0 ||
        (subject?.display && patientSearchString === subject.display)
      ) {
        setNewPatientOption(false);
      } else {
        setNewPatientOption(true);
      }
    } catch (e) {
      console.error("Error while searching ", e);
    }
  };

  const createAndAddPatient = async (patient, createDuplicate) => {
    const payload = {
      ...patient,
      name: [
        {
          ...patient.name[0],
          firstName: patient.name[0].firstName?.trim(),
          family: patient.name[0].family?.trim(),
        },
      ],
    };

    payload.managingOrganization = unionBy(
      payload.managingOrganization || [],
      [organizationReference],
      "reference",
    );
    try {
      const createdPatient = await patientApi.savePatient(
        payload,
        createDuplicate,
      );
      onPatientSelected(createdPatient);
      setNotification({
        message: patientCreatedMessage,
        variant: NotificationVariant.success,
      });
      resetPatientForm(false);
      closeDuplicatePatientModal();
    } catch (e) {
      if (e.body) {
        setPatientForCreation(payload);
        setAllowNewPatient(false);
        setDuplicatePatients(e.body);
        openDuplicatePatientModal();
      } else {
        setNotification({
          message: "An error occurred while creating a new patient",
          variant: NotificationVariant.error,
        });
      }
    }
  };

  // Keep the controlled Autocomplete input in sync when subject is set
  // externally (e.g. AiNoteCreateModal initialPatient).
  useEffect(() => {
    if (subject?.display) {
      setPatientSearchString(subject.display);
    }
  }, [subject?.identifier, subject?.display]);

  useAsyncEffect(async () => {
    if (subject && checkOpenEncounters) {
      await findEncountersForSubject(subject);
    }
  }, [subject, checkOpenEncounters]);

  const updatePatient = (field, value) => {
    const update = { ...newPatient };
    set(update, field, value);
    setNewPatient(update);
    setPatientForCreation(update);
  };

  const resetPatientForm = (optionForNew) => {
    setNewPatient(defaultPatient);
    setAllowNewPatient(false);
    setNewPatientOption(optionForNew);
  };

  const getPatientOptionLabel = (option) => {
    // if no name, assume option is a reference
    if (!option.name) return option?.display ?? "";

    // otherwise assume it's a name object
    const name = generateHumanDisplayName(get(option, "name[0]"));
    const gender = !option.gender
      ? undefined
      : startCase(option.gender.toLowerCase());
    const birthDate = option?.birthDate
      ? new Date(option.birthDate)
      : undefined;
    const age = birthDate
      ? displayAge(birthDate, new Date(), option?.deceased)
      : undefined;
    const phone = option?.telecom?.find(
      (t) => t.system === "phone" && t.value,
    )?.value;
    const address = get(option, "address[0].line");
    const label = [name, gender, formatDate(birthDate), age, phone, address]
      .filter((e) => e && e !== "")
      .join(" - ");

    return label;
  };

  const onPatientSelected = (val) => {
    if (val) {
      if (val.reference && !val.name) {
        const label = val.display ?? "";
        setSubject(val);
        // Keep controlled inputValue in sync so the field shows the selection
        // (e.g. after "Use Selected Patient" from the duplicate modal).
        setPatientSearchString(label);
        setNewPatientOption(false);
        const match = patientSearchResults.find(
          (p) => p.id === val.identifier || p.id === val.id,
        );
        onPatientChange?.(match);
      } else {
        const label = getPatientOptionLabel(val);
        const patient: Reference = {
          type: "Patient",
          reference: `Patient/${val?.id}`,
          identifier: val?.id,
          // Persist name only; demographics stay in the field/dropdown label.
          display: generateHumanDisplayName(get(val, "name[0]")),
        };

        setSubject(patient);
        setPatientSearchString(label);
        setNewPatientOption(false);
        onPatientChange?.(val as Patient);
      }
    } else {
      // cleared
      setSubject(undefined);
      setPatientSearchString("");
      setNewPatientOption(false);
      onPatientChange?.(undefined);
      // Drop any open-encounter id from the previous patient.
      setCurrentEncounterId?.(undefined);
    }
  };

  const encounterStatuses = ["arrived", "in-progress", "triaged"];

  const findEncountersForSubject = async (patient) => {
    if (!patient) {
      onPatientSelected(undefined);
      setEncounterSearchComplete?.(true);
      return;
    }

    if (!checkOpenEncounters) {
      onPatientSelected(patient);
      setEncounterSearchComplete?.(true);
      return;
    }

    try {
      setComponentLoading(true);
      const result = await medicalsApi.findEncounters({
        patient: patient?.id || patient?.identifier,
        practitioner: practitionerReference?.identifier,
        "service-provider": organizationReference?.reference,
        status: encounterStatuses,
      });

      setCurrentEncounterId?.(result[0]?.id);

      onPatientSelected(patient);

      setEncounterSearchComplete?.(true);
    } catch (error) {
      setNotification({
        message: "An error occured while searching patientEncounter",
        variant: NotificationVariant.error,
      });
    } finally {
      setComponentLoading(false);
    }
  };

  useEffect(() => {
    searchForPatient();
    if (
      (!patientSearchString || patientSearchString.length === 0) &&
      newPatientOption
    ) {
      setNewPatientOption(false);
    }
  }, [patientSearchString, newPatientOption]);

  return (
    <>
      {!allowNewPatient && (
        <Grid
          item
          xs={newPatientOption ? 10 : 12}
          sx={{
            transition: (theme) =>
              theme.transitions.create("all", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
          }}
        >
          <Autocomplete
            fullWidth
            autoHighlight
            key="medical-encounter-patient-lookup"
            id="medical-encounter-patient-lookup"
            options={patientSearchResults}
            value={subject}
            getOptionLabel={getPatientOptionLabel}
            onInputChange={(e, newInput, reason) => {
              setPatientSearchString(newInput);
              // Clearing the field (X or deleting all text) must drop the selection
              // so callers (e.g. Create AI Note) can disable submit.
              if (
                reason === "clear" ||
                (reason === "input" && newInput === "")
              ) {
                if (subject) {
                  onPatientSelected(undefined);
                }
                return;
              }
              // Editing away from the selected label invalidates the selection.
              // Keep the typed search string; clear subject + stale encounter id.
              if (
                reason === "input" &&
                subject?.display &&
                newInput !== subject.display
              ) {
                setSubject(undefined);
                onPatientChange?.(undefined);
                setCurrentEncounterId?.(undefined);
              }
            }}
            freeSolo
            inputValue={patientSearchString}
            onChange={async (e, v) => {
              await findEncountersForSubject(v);
            }}
            filterSelectedOptions
            filterOptions={(x) => x}
            isOptionEqualToValue={(o, v) => o?.id === v?.identifier}
            renderInput={(params) => (
              <TextField
                required
                {...params}
                label="Patient"
                variant="outlined"
                placeholder="Search by patient. If none is found, create a new one."
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
      )}
      {newPatientOption && !allowNewPatient && (
        <Fade in={newPatientOption} timeout={550}>
          <Grid item xs={2}>
            <Link
              color="primary"
              onClick={(e) => {
                setDuplicatePatientActions({
                  useDuplicatePatient: (patient) => {
                    onPatientSelected(patient);
                    resetPatientForm(false);
                    closeDuplicatePatientModal();
                  },
                  actionButtons: [
                    {
                      button: ({ onClick }) => (
                        <Button
                          color="primary"
                          variant="contained"
                          onClick={onClick}
                        >
                          Create New Patient
                        </Button>
                      ),
                      onClick: (newPatient) => {
                        createAndAddPatient(newPatient, true);
                      },
                    },
                  ],
                });
                setSubject(undefined);
                onPatientChange?.(undefined);
                setCurrentEncounterId?.(undefined);
                setAllowNewPatient(true);
                setNewPatientOption(false);
                const names = patientSearchString.split(" ");
                if (names[0]) {
                  updatePatient("name[0].firstName", names[0]);
                }
                if (names[1]) {
                  updatePatient("name[0].family", names[1]);
                }
                updatePatient("managingOrganization", [organizationReference]);
              }}
            >
              <Typography sx={{ textAlign: "center", cursor: "pointer" }}>
                Create New patient
              </Typography>
            </Link>
          </Grid>
        </Fade>
      )}
      {allowNewPatient && (
        <PatientInput
          newPatient={newPatient}
          handleUpdate={updatePatient}
          handleCreate={createAndAddPatient}
          handleReset={resetPatientForm}
          btnText={addPatientButtonText}
          dobRequired
          genderRequired
        />
      )}
    </>
  );
};

export default FindOrCreatePatientInput;
